import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { SampleBadge } from "../components/common/SampleBadge";
import { LoanFormDialog } from "../components/loans/LoanFormDialog";
import { LoanViewDialog } from "../components/loans/LoanViewDialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { CLFs, VOs, SHGs, Members, Loans } from "../lib/api";
import { sampleLoans } from "../lib/sampleData";
import { formatINR, formatIndianDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Eye } from "lucide-react";

const STATUS_LABEL = { active: "Active", pending: "Pending", closed: "Closed" };
const byId = (list, f = (x) => x.name) => Object.fromEntries(list.map((x) => [x.id, f(x)]));

const LoansPage = () => {
  const { t } = useLang();
  const [loans, setLoans] = useState([]);
  const [refs, setRefs] = useState({ clfs: [], vos: [], shgs: [], members: [] });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [showDemo, setShowDemo] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, clfs, vos, shgs, members] = await Promise.all([Loans.list(), CLFs.list(), VOs.list(), SHGs.list(), Members.list()]);
      setLoans(l); setRefs({ clfs, vos, shgs, members });
    } catch (e) { toast.error(e.message || "Failed to load loans"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const names = useMemo(() => ({ clf: byId(refs.clfs), vo: byId(refs.vos), shg: byId(refs.shgs), member: byId(refs.members) }), [refs]);
  const rows = useMemo(() => loans.map((l) => ({ ...l, shgName: names.shg[l.shg_id] || "", memberName: l.member_id ? names.member[l.member_id] || "" : "" })), [loans, names]);

  const save = async (payload) => {
    if (editing) { await Loans.update(editing.id, payload); toast.success("Loan updated"); }
    else { const l = await Loans.create(payload); toast.success(`Loan ${l.loan_number} created`); }
    await load();
  };

  const columns = [
    { key: "loan_number", header: "Loan ID", mono: true, strong: true },
    { key: "shgName", header: "SHG" },
    { key: "memberName", header: "Borrower", render: (r) => r.memberName || <span className="text-emerald-900/50">SHG</span> },
    { key: "principal", header: "Principal", align: "right", mono: true, render: (r) => formatINR(r.principal) },
    { key: "disbursement_date", header: "Disbursed", mono: true, render: (r) => formatIndianDate(r.disbursement_date) },
    { key: "interest_rate", header: "Rate", mono: true, render: (r) => `${r.interest_rate}% ${r.interest_method === "flat" ? "flat" : "red."}` },
    { key: "tenure_months", header: "Tenure", align: "right", mono: true, render: (r) => `${r.tenure_months} mo` },
    { key: "emi", header: "EMI", align: "right", mono: true, render: (r) => formatINR(r.emi) },
    { key: "first_emi_date", header: "1st EMI", mono: true, render: (r) => formatIndianDate(r.first_emi_date) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={STATUS_LABEL[r.status]} /> },
    { key: "_a", header: "Actions", render: (r) => (
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`loan-view-${r.id}`} onClick={() => setViewing(r)}><Eye className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`loan-edit-${r.id}`} onClick={() => { setEditing(r); setFormOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];

  const demoColumns = [
    { key: "id", header: "Demo ID", mono: true }, { key: "shg", header: "SHG" }, { key: "purpose", header: "Purpose" },
    { key: "principal", header: "Principal", align: "right", mono: true, render: (r) => formatINR(r.principal) },
    { key: "emi", header: "EMI", align: "right", mono: true, render: (r) => formatINR(r.emi) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  const noSetup = !refs.shgs.length;

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader title={t.nav.loans} subtitle="Loans saved in your database" testId="loans-page-header" showSampleBadge={false}
        actions={
          <Button data-testid="loan-add-btn" disabled={noSetup} onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0F4C3A] text-white hover:bg-emerald-900">
            <Plus className="mr-1.5 h-4 w-4" />Add Loan
          </Button>
        } />

      {!loading && noSetup && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="loan-setup-notice">
          Register at least one CLF, VO and SHG before adding loans.
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50">Loading…</div>
      ) : (
        <DataTable columns={columns} rows={rows} searchKeys={["loan_number", "shgName", "memberName", "purpose", "status"]}
          testId="loans-table" emptyLabel="No loans saved yet. Click “Add Loan” to record the first one." />
      )}

      <div className="mt-8 rounded-2xl border border-dashed border-[#D95D39]/40 p-4 sample-data-stripe" data-testid="loans-demo-section">
        <label className="flex items-center gap-3 text-sm text-emerald-900/80">
          <Switch checked={showDemo} onCheckedChange={setShowDemo} data-testid="loans-demo-toggle" />
          Show demo records <SampleBadge />
          <span className="text-xs text-emerald-900/50">Not real data · not saved · excluded from totals</span>
        </label>
        {showDemo && <div className="mt-4"><DataTable columns={demoColumns} rows={sampleLoans} searchKeys={["id", "shg", "purpose"]} testId="loans-demo-table" /></div>}
      </div>

      <LoanFormDialog open={formOpen} onOpenChange={setFormOpen} loan={editing} refs={refs} onSubmit={save} />
      {viewing && <LoanViewDialog loan={viewing} names={names} onOpenChange={() => setViewing(null)} />}
    </div>
  );
};

export default LoansPage;
